import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ArrowRight, AlertTriangle, Zap, Timer, HardHat,
  Layers, Activity, ShieldCheck, CheckCircle2,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

/* bold-parser */
const B = ({ t, cls = "text-ink font-semibold" }) => (
  <>
    {t.split(/(\*\*[^*]+\*\*)/g).map((s, i) =>
      s.startsWith("**") && s.endsWith("**")
        ? <strong key={i} className={cls}>{s.slice(2, -2)}</strong>
        : <span key={i}>{s}</span>
    )}
  </>
);

/* illustrative safety metrics */
const EXPOSURE_CUT = 50;     // % reduction in field-assembly / exposure time
const FEWER_PIECES = 60;     // % fewer components

const PAINS = [
  "Crews log **hours** assembling **complex, multi-piece** locking systems.",
  "Field assembly sits **beside live pipelines** and **high-voltage lines.**",
  "**Warped, rutted mats** create **trip and tripping-fall hazards.**",
  "Manual handling drives **pinch-point** and **lift-zone** exposure.",
  "Every added minute is an added **exposure hour.**",
];

const SOLUTIONS = [
  {
    icon: Timer,
    title: "Easy Install — Compress the Exposure Window",
    rows: [
      "**Few-piece** locking system.",
      `Field-assembly time **cut ~${EXPOSURE_CUT}%.**`,
      "**Fewer minutes** inside the hazard zone.",
      "**No field engineering** required.",
    ],
  },
  {
    icon: HardHat,
    title: "Handling Safety — Keep Crews Clear",
    rows: [
      "**High strength-to-weight** ratio.",
      "Set by **skid steer / telehandler.**",
      "Crews clear of **heavy pinch points.**",
      "**No crane lift zones** for placement.",
    ],
  },
  {
    icon: Layers,
    title: "Flat-Lay Integrity — A Trip-Free Surface",
    rows: [
      "**Thermal stability** prevents warping.",
      "**No rutting** under repeated loads.",
      "**Flush, gap-free** interface.",
      "Compliant walking & driving surface.",
    ],
  },
];

const OUTCOMES = [
  { icon: ShieldCheck, v: "Zero", l: "field-engineering failures" },
  { icon: Timer, v: `−${EXPOSURE_CUT}%`, l: "exposure hours in high-risk zones" },
  { icon: Activity, v: "TRIR ↓", l: "measurable drop during mobilization" },
];

export default function SafetyBrief() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">HSE / Safety Brief · One-Page</span>
            <a href="/big-bad-mats-safety-brief.pdf" download data-testid="download-safety-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-safety-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="safety-brief-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">For the Safety / HSE Director · Industrial & Energy</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[32px]">
                Compress the Exposure Window. Eliminate the Trip Hazard.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                One metric governs mobilization risk: <span className="text-flame font-semibold">ground-crew exposure hours</span> in high-risk zones. Fewer pieces. Faster set. <span className="font-semibold text-ink">Fewer hours near the hazard.</span>
              </p>
            </div>
            <div className="w-[32%] bg-ink text-white p-3">
              <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">The safety math</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="font-display font-extrabold text-flame text-[22px] leading-none">−{EXPOSURE_CUT}%</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">exposure hours</div>
                </div>
                <div>
                  <div className="font-display font-extrabold text-flame text-[22px] leading-none">−{FEWER_PIECES}%</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">assembly components</div>
                </div>
              </div>
            </div>
          </header>

          {/* body: pain (left) + solutions (right) */}
          <div className="flex-1 grid grid-cols-5 gap-4 pt-4 min-h-0">
            {/* pain */}
            <div className="col-span-2 border border-border flex flex-col">
              <div className="bg-charcoal text-white px-4 py-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-flame" />
                <span className="font-display font-bold text-[12px] uppercase tracking-wider">Exposure Risks on the Ground</span>
              </div>
              <ul className="p-4 space-y-3 flex-1 flex flex-col justify-center">
                {PAINS.map((p, i) => (
                  <li key={p} className="flex items-start gap-2.5 text-charcoal text-[11px] leading-snug">
                    {i === 1
                      ? <Zap className="h-4 w-4 text-flame flex-shrink-0 mt-0.5" />
                      : <span className="mt-1.5 h-1.5 w-1.5 bg-charcoal/40 flex-shrink-0" />}
                    <span><B t={p} cls="text-ink font-semibold" /></span>
                  </li>
                ))}
              </ul>
            </div>

            {/* solutions */}
            <div className="col-span-3 grid grid-rows-3 gap-3">
              {SOLUTIONS.map((s) => (
                <div key={s.title} className="border-2 border-flame flex flex-col">
                  <div className="bg-flame text-white px-4 py-1.5 flex items-center gap-2">
                    <s.icon className="h-4 w-4" />
                    <span className="font-display font-bold text-[11.5px] uppercase tracking-wider">{s.title}</span>
                  </div>
                  <ul className="px-4 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5 flex-1 content-center">
                    {s.rows.map((x) => (
                      <li key={x} className="flex items-start gap-2 text-charcoal text-[10.5px] leading-snug">
                        <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" />
                        <span><B t={x} /></span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* safety outcome breakout */}
          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-flame" />
              <span className="font-display font-bold text-[13px] uppercase tracking-wider">The Safety Outcome</span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3">
              {OUTCOMES.map((o) => (
                <div key={o.l} className="bg-white/5 border border-white/10 px-4 py-2.5 flex items-center gap-3">
                  <o.icon className="h-6 w-6 text-flame flex-shrink-0" />
                  <div>
                    <div className="font-display font-extrabold text-flame text-[19px] leading-none">{o.v}</div>
                    <div className="text-white/60 text-[9.5px] mt-0.5">{o.l}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA footer */}
          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div>
              <div className="font-display font-extrabold text-ink text-[15px] leading-tight">Quantify the exposure reduction for your next mobilization.</div>
              <div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <Link to="/request-quote" className="inline-flex items-center gap-1.5 bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">
              Request the Site Safety &amp; Field Mechanics Data Sheet <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Exposure, component, and TRIR figures are representative — validated against your site mobilization plan during assessment. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
